import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import secureStorage from '../services/secureStorage';
import encryptedSessionStorage from '../services/encryptedSessionStorage';

export const TECHNICIAN_STORAGE_KEY = 'selected_technician';
export const CREATED_USER_ID_KEY = 'created_user_id';
export const DEFAULT_STORE_ROOM_KEY = 'default_store_room';

export interface SelectedTechnician {
  selectedTechnicianId: number | string;
  selectedTechnicianName: string;
  /** The BFF roleId returned by /api/auth/validate — used to fetch permissions
   *  via GET /api/technicians/{roleId}/permissions.  Distinct from
   *  selectedTechnicianId (the technician record ID). */
  selectedTechnicianRoleId?: number;
}

export interface TechnicianPermissions {
  canCreateWorkOrders: boolean;
  isApprovedAboveThresholdEnabled: boolean;
  isDowntimeTimeTrackingEnabled: boolean;
  canUpdateWorkOrderDepartment: boolean;
  canAuthorizeDocument: boolean;
  canOverrideMeter: boolean;
}

/** A single entry from the BFF `listOfPermissions` array. */
export interface Permission {
  permissionCode: string;
  friendlyName: string;
  hasPermission: boolean;
}

interface TechnicianState {
  selectedTechnician: SelectedTechnician;
  createdUserId: string | number;
  defaultStoreroom: string;
  permissions: TechnicianPermissions;
  /**
   * Permissions fetched when the admin logs in via the agency login flow
   * (ShopHomePage → GET /api/technicians/{roleId}/permissions using the
   * admin's own roleId from /api/auth/validate).
   * Cleared on ShopHomePage logout.
   */
  shophomepagePermissions: Permission[];
  /**
   * Permissions fetched when a technician enters their PIN and the dashboard
   * mounts (MainLayoutScreen → GET /api/technicians/{roleId}/permissions using
   * the selected technician's ID).
   * Cleared on dashboard logout / when a new technician is selected.
   */
  dashboardPermissions: Permission[];
  technicianThumbnail: string;
}

// Synchronous read for Redux initialState — reads the raw value from sessionStorage.
// Legacy plaintext values are parsed as-is; encrypted ciphertext will throw (caught → default).
// The AppNavigator bootstrap overwrites this with the fully-decrypted secureStorage value.
export function getPersistedTechnician(): SelectedTechnician {
  try {
    const stored = encryptedSessionStorage.getItemSync(TECHNICIAN_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as SelectedTechnician;
    }
  } catch {
    // ignore parse errors / encrypted ciphertext
  }
  return { selectedTechnicianId: '', selectedTechnicianName: '' };
}

export function getPersistedCreatedUserId(): string | number {
  try {
    const stored = encryptedSessionStorage.getItemSync(CREATED_USER_ID_KEY);
    if (stored && stored !== '0') {
      return stored;
    }
  } catch {
    // ignore
  }
  return 0;
}

export function getPersistedDefaultStoreRoom(): string {
  try {
    const stored = encryptedSessionStorage.getItemSync(DEFAULT_STORE_ROOM_KEY);
    if (stored && stored !== '0') {
      return stored;
    }
  } catch {
    // ignore
  }
  return '';
}

const initialState: TechnicianState = {
  selectedTechnician: getPersistedTechnician(),
  createdUserId: getPersistedCreatedUserId(),
  defaultStoreroom: getPersistedDefaultStoreRoom(),
  permissions: {
    canCreateWorkOrders: true,
    isApprovedAboveThresholdEnabled: true,
    isDowntimeTimeTrackingEnabled: false,
    canUpdateWorkOrderDepartment: true,
    canAuthorizeDocument: true,
    canOverrideMeter: true,
  },
  shophomepagePermissions: [],
  dashboardPermissions: [],
  technicianThumbnail: '',
};

const technicianSlice = createSlice({
  name: 'technician',
  initialState,
  reducers: {
    setSelectedTechnicianData(state, action: PayloadAction<SelectedTechnician>) {
      state.selectedTechnician = action.payload;
      // Persist to secureStorage on all platforms (encrypted)
      secureStorage.setItem(TECHNICIAN_STORAGE_KEY, JSON.stringify(action.payload)).catch(() => {});
    },
    setCreatedUserId(state, action: PayloadAction<string | number>) {
      state.createdUserId = action.payload;
      // Persist to secureStorage on all platforms (encrypted)
      secureStorage.setItem(CREATED_USER_ID_KEY, String(action.payload)).catch(() => {});
    },
    setDefaultStoreRoom(state, action: PayloadAction<string>) {
      state.defaultStoreroom = action.payload;
      // Persist to secureStorage on all platforms (encrypted)
      secureStorage.setItem(DEFAULT_STORE_ROOM_KEY, String(action.payload)).catch(() => {});
    },
    setTechnicianPermissions(state, action: PayloadAction<TechnicianPermissions>) {
      state.permissions = action.payload;
    },
    /** Store the admin-login permissions (ShopHomePage scope). */
    setShophomepagePermissions(state, action: PayloadAction<Permission[]>) {
      state.shophomepagePermissions = action.payload;
    },
    /** Clear ShopHomePage-scoped permissions on admin logout. */
    clearShophomepagePermissions(state) {
      state.shophomepagePermissions = [];
    },
    /** Store the technician-dashboard permissions (MainLayoutScreen scope). */
    setDashboardPermissions(state, action: PayloadAction<Permission[]>) {
      state.dashboardPermissions = action.payload;
    },
    /** Clear dashboard-scoped permissions on technician logout or new technician selection. */
    clearDashboardPermissions(state) {
      state.dashboardPermissions = [];
    },
    setTechnicianThumbnail(state, action: PayloadAction<string>) {
      state.technicianThumbnail = action.payload;
    },
    clearSelectedTechnicianData(state) {
      state.selectedTechnician = { selectedTechnicianId: '', selectedTechnicianName: '' };
      state.createdUserId = 0;
      state.defaultStoreroom = '';
      state.technicianThumbnail = '';
      // Clear dashboard permissions when the technician session ends.
      // shophomepagePermissions are intentionally NOT cleared here — the admin
      // remains logged in on ShopHomePage and needs those permissions intact.
      state.dashboardPermissions = [];
      state.permissions = {
        canCreateWorkOrders: true,
        isApprovedAboveThresholdEnabled: true,
        isDowntimeTimeTrackingEnabled: false,
        canUpdateWorkOrderDepartment: true,
        canAuthorizeDocument: true,
        canOverrideMeter: true,
      };
      secureStorage.removeItem(TECHNICIAN_STORAGE_KEY).catch(() => {});
      secureStorage.removeItem(CREATED_USER_ID_KEY).catch(() => {});
      secureStorage.removeItem(DEFAULT_STORE_ROOM_KEY).catch(() => {});
    },
  },
});

export const {
  setSelectedTechnicianData,
  setCreatedUserId,
  clearSelectedTechnicianData,
  setDefaultStoreRoom,
  setTechnicianPermissions,
  setShophomepagePermissions,
  clearShophomepagePermissions,
  setDashboardPermissions,
  clearDashboardPermissions,
  setTechnicianThumbnail,
} = technicianSlice.actions;
export default technicianSlice.reducer;
